import {
  DAY_MS,
  inTimeRange,
  trailingRange,
  type ProgressWidgetId,
  type TimedSessionResult,
  type TimeRange,
} from "@heraion/shared";

import type { ProgressDeps } from "./types";

// PG-64: Progress answers «how am I doing now». Each widget has its own
// window, chosen here and not by the athlete; the report picks free periods.
// Coverage keeps its calendar week; Rhythm, Streak and volume by tag their
// last PROGRESS_WEEK_BARS calendar weeks (on WeekBarsCard, PG-64 fetta 2);
// the intensity heatmap and the PR timeline their whole history.

/** How many calendar weeks a Progress bar series shows (Rhythm, Streak,
 *  volume by tag). The report keeps its whole period (ProgressRhythmCard,
 *  scope "period"). */
export const PROGRESS_WEEK_BARS = 8;

const MINUTE_MS = 60_000;

/** Now, rounded up to the minute: a window that ends here gives the same
 *  numbers on every render of that minute, so the panels do not recompute
 *  on each one (Progress, the coach detail). */
export const nowToTheMinute = (): number =>
  Math.ceil(Date.now() / MINUTE_MS) * MINUTE_MS;

/** The window of a panel mounted without a range (Home, the coach detail).
 *  Their behaviour predates PG-64 and stays as it was. */
export const DEFAULT_WINDOW_DAYS = 90;

/** The fixed trailing window, in days, of each Progress widget that takes a
 *  range (measurement-contract.md §PG-64, the critic's table). */
export const PROGRESS_WINDOW_DAYS = {
  e1rmTrends: 84,
  repsAtLoad: 84,
  movementBalance: 28,
  painVolume: 28,
  exerciseFrequency: 28,
} as const satisfies Partial<Record<ProgressWidgetId, number>>;
export type WindowedProgressWidget = keyof typeof PROGRESS_WINDOW_DAYS;

/** The range a panel computes over. The fallback reads the clock, so call it
 *  inside the memo that uses it, keyed on `range` itself. */
export const panelRange = (range: TimeRange | undefined): TimeRange =>
  range ?? trailingRange(Date.now(), DEFAULT_WINDOW_DAYS);

/** The window's length in whole days, at least 1, for the panel's labels and
 *  math. A report from Monday 00:00 to Sunday 23:59:59.999 is 7. */
export const panelDays = (range: TimeRange | undefined): number =>
  range
    ? Math.max(1, Math.round((range.toMs - range.fromMs) / DAY_MS))
    : DEFAULT_WINDOW_DAYS;

/** A report's period with no session in it. Only then does a panel say «No
 *  training in this period»: sessions with nothing to draw keep Progress's
 *  sentence. Always false outside a report (no `resultsRange`). */
export const isEmptyReportPeriod = (
  deps: Pick<ProgressDeps, "range" | "resultsRange">,
  results: ReadonlyArray<TimedSessionResult> | null,
): boolean =>
  deps.resultsRange !== undefined &&
  !(results ?? []).some((r) => inTimeRange(r.startedAtMs, panelRange(deps.range)));
